/**
 * ============================================================================
 * METRIC COMMENTS API — accessor for /listMetricComments + /publishMetricComment.
 * ============================================================================
 *
 * LAYER: accessor. Owns transport for the callout/comment/reply feature and
 * the record→UI mapping. Ported from the legacy CTS UI
 * (ADSProjectPortalUI MetricCalloutsApi), adapted to the HILT contract:
 * a cell is (dashboardId, reviewType, metricKey, reportingPeriod), and auth is
 * the shared Midway/SigV4 path (ReportingApiClient), not a userAlias header.
 *
 * `actor` is required non-blank on every write and carries the caller's
 * Midway alias (accessors/currentUser). The backend derives the STORED author
 * from its authenticated identity and overrides a mismatch, so this is not a
 * trust boundary — sending the matching alias just keeps the request honest
 * (and the server's mismatch WARN quiet).
 *
 * Cache policy (keys, invalidation) is NOT here — it belongs to the RTK
 * endpoint (features/api/metricCommentsApi.ts).
 */

import {
  ErrorCode,
  ReportingApiClient,
  ReportingApiError,
} from 'src/accessors/ReportingApiClient';
import { WBR_REVIEW_TYPE } from 'src/constants';
import { recordPaginationChain } from 'src/metrics/performanceMetrics';
import {
  CALLOUTS_MAX_PAGES,
  CALLOUTS_PAGE_SIZE,
  LABEL_UNKNOWN,
} from 'src/constants/metricCallouts';
import {
  currentUserAlias,
  learnActorFrom,
  rememberOwnRecord,
} from 'src/accessors/currentUser';
import {
  CellAnchor,
  CellCalloutSummary,
  CellComments,
  Comment,
  CommentReply,
  ListMetricCommentsInput,
  ListMetricCommentsOutput,
  MetricCommentRecord,
  MetricCommentReplyRecord,
  PublishMetricCommentInput,
  PublishMetricCommentOutput,
} from 'src/types/metricComments.types';
import { DataMode } from 'src/types/metricsApi.types';

const toReply = (r: MetricCommentReplyRecord): CommentReply => ({
  id: r.id,
  author: r.updatedByActor || r.createdByActor || LABEL_UNKNOWN,
  body: r.content,
  createdAt: r.createdAt,
  updatedAt: r.updatedAt,
});

/**
 * The cell's network on a wire body when known (CTS `metricCategory`). Sent
 * unconditionally — Coral ignores members it doesn't model, so this is a
 * no-op against today's backend and starts taking effect the moment the model
 * adds `metricCategory`. No feature flag to flip.
 */
const networkField = (metricCategory?: string): { metricCategory?: string } =>
  metricCategory ? { metricCategory } : {};

/**
 * Feed a list response back to `currentUser` so it can read our own alias off a
 * record we authored. The client's `actor` is a guess — the backend overrides it
 * with its authenticated Midway identity — so the stored author is the ONLY
 * place the real alias appears, and the author-only Edit/Delete gate needs it.
 * Replies are included: a reply may be the only thing the user has written.
 */
function learnOwnActor(records: MetricCommentRecord[]): void {
  const pairs: Array<{ id: string; actor: string }> = [];
  for (const rec of records) {
    pairs.push({
      id: rec.id,
      actor: rec.updatedByActor || rec.createdByActor || '',
    });
    for (const reply of rec.replies ?? []) {
      pairs.push({
        id: reply.id,
        actor: reply.updatedByActor || reply.createdByActor || '',
      });
    }
  }
  learnActorFrom(pairs);
}

const toComment = (c: MetricCommentRecord): Comment => ({
  id: c.id,
  author: c.updatedByActor || c.createdByActor || LABEL_UNKNOWN,
  body: c.content,
  createdAt: c.createdAt,
  updatedAt: c.updatedAt,
  replies: (c.replies || []).map(toReply),
});

class MetricCommentsApiClient extends ReportingApiClient {
  private publish(
    input: PublishMetricCommentInput,
  ): Promise<PublishMetricCommentOutput> {
    return this.post<PublishMetricCommentOutput>(
      '/publishMetricComment',
      input,
    );
  }

  private list(
    input: ListMetricCommentsInput,
  ): Promise<ListMetricCommentsOutput> {
    return this.post<ListMetricCommentsOutput>('/listMetricComments', input);
  }

  /**
   * Every record for a scope, following paginationKey (bounded). `metricKey`
   * omitted scans the whole reporting period (all cells).
   */
  private async fetchAll(
    dashboardId: string,
    reportingPeriod: string,
    metricKey?: string,
    metricCategory?: string,
  ): Promise<MetricCommentRecord[]> {
    const all: MetricCommentRecord[] = [];
    let paginationKey: string | undefined;
    const chainStartedAt = performance.now();
    for (let pageNo = 0; pageNo < CALLOUTS_MAX_PAGES; pageNo++) {
      const page = await this.list({
        dashboardId,
        reviewType: WBR_REVIEW_TYPE,
        reportingPeriod,
        metricKeys: metricKey ? [metricKey] : undefined,
        ...networkField(metricCategory),
        pageSize: CALLOUTS_PAGE_SIZE,
        paginationKey,
      });
      all.push(...(page.comments ?? []));
      paginationKey = page.paginationKey || undefined;
      // Terminate only when the backend signals no more pages. Returning here
      // (rather than a `while` cap that silently drops a trailing key) means
      // callers never see a partially loaded scan — matches GetMetricsApi.
      if (!paginationKey) {
        recordPaginationChain('ListMetricComments', pageNo + 1, performance.now() - chainStartedAt);
        learnOwnActor(all);
        return all;
      }
    }
    // A key still remaining at the cap means the scan is incomplete; a whole-week
    // scanWeek would otherwise drop callouts and under-report the roll-up/badges.
    // Fail loudly instead, exactly as getMetricsAllPages does.
    throw new ReportingApiError(
      500,
      ErrorCode.InternalFailure,
      `listMetricComments pagination did not terminate within ${CALLOUTS_MAX_PAGES} pages`,
    );
  }

  /**
   * Roll up every cell that carries a callout or comment in one reporting
   * period — the week-level panel's data. One entry per metricKey with its
   * callout preview and comment count; display names are joined from the
   * catalog by the caller.
   */
  public async scanWeek(
    dashboardId: string,
    reportingPeriod: string,
    _dataMode: DataMode,
  ): Promise<CellCalloutSummary[]> {
    const all = await this.fetchAll(dashboardId, reportingPeriod);
    // Roll up per cell. A cell is (metricCategory, metricKey) once records carry
    // a network; until the backend adds it, records have no metricCategory and
    // this collapses to per-metricKey — the current behaviour, data-driven, no
    // flag.
    const byCell = new Map<string, CellCalloutSummary>();
    for (const rec of all) {
      const key = `${rec.metricCategory ?? ''}#${rec.metricKey}`;
      const summary = byCell.get(key) ?? {
        metricKey: rec.metricKey,
        metricCategory: rec.metricCategory,
        reportingPeriod,
        commentCount: 0,
        lastAuthor: '',
        lastUpdatedAt: '',
      };
      if (rec.isCallout) {
        summary.calloutPreview = rec.content;
      } else {
        summary.commentCount += 1;
      }
      if (rec.updatedAt > summary.lastUpdatedAt) {
        summary.lastUpdatedAt = rec.updatedAt;
        summary.lastAuthor =
          rec.updatedByActor || rec.createdByActor || LABEL_UNKNOWN;
      }
      byCell.set(key, summary);
    }
    return Array.from(byCell.values()).sort((a, b) =>
      b.lastUpdatedAt.localeCompare(a.lastUpdatedAt),
    );
  }

  /** The callout (if any) and comments for one cell, split and sorted for the drawer. */
  public async loadCell(anchor: CellAnchor): Promise<CellComments> {
    const all = await this.fetchAll(
      anchor.dashboardId,
      anchor.reportingPeriod,
      anchor.metricKey,
      anchor.metricCategory,
    );
    const calloutRecord = all.find((c) => c.isCallout);
    const comments = all
      .filter((c) => !c.isCallout)
      .sort((x, y) => x.createdAt.localeCompare(y.createdAt))
      .map(toComment);
    return {
      comments,
      callout: calloutRecord
        ? {
            id: calloutRecord.id,
            body: calloutRecord.content,
            lastEditedBy:
              calloutRecord.updatedByActor ||
              calloutRecord.createdByActor ||
              LABEL_UNKNOWN,
            lastEditedAt: calloutRecord.updatedAt,
          }
        : null,
    };
  }

  // ── Callout (one per cell) ──
  public async writeCallout(
    anchor: CellAnchor,
    body: string,
    existingId: string | null,
  ): Promise<void> {
    const written = await this.publish(
      existingId
        ? {
            id: existingId,
            type: 'CALLOUT',
            dashboardId: anchor.dashboardId,
            reviewType: WBR_REVIEW_TYPE,
            reportingPeriod: anchor.reportingPeriod,
            content: body,
            actor: currentUserAlias(),
          }
        : {
            type: 'CALLOUT',
            dashboardId: anchor.dashboardId,
            reviewType: WBR_REVIEW_TYPE,
            metricKey: anchor.metricKey,
            ...networkField(anchor.metricCategory),
            reportingPeriod: anchor.reportingPeriod,
            content: body,
            actor: currentUserAlias(),
          },
    );
    // The callout headline is editable by anyone (CTS parity), so this is not
    // for a gate — it is how the next list response teaches us our own alias,
    // which the COMMENT/REPLY gates do need.
    rememberOwnRecord(existingId ?? written?.id);
  }

  public async deleteCallout(id: string, anchor: CellAnchor): Promise<void> {
    await this.publish({
      id,
      type: 'CALLOUT',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      isDeleted: true,
      actor: currentUserAlias(),
    });
  }

  // ── Comments (many per cell) ──
  public async createComment(anchor: CellAnchor, body: string): Promise<void> {
    const written = await this.publish({
      type: 'COMMENT',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      metricKey: anchor.metricKey,
      ...networkField(anchor.metricCategory),
      reportingPeriod: anchor.reportingPeriod,
      content: body,
      actor: currentUserAlias(),
    });
    // Records the comment as ours, so its Edit/Delete appear on the very next
    // render — before any list response has taught us our alias.
    rememberOwnRecord(written?.id);
  }

  public async editComment(
    id: string,
    body: string,
    anchor: CellAnchor,
  ): Promise<void> {
    await this.publish({
      id,
      type: 'COMMENT',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      content: body,
      actor: currentUserAlias(),
    });
  }

  public async deleteComment(id: string, anchor: CellAnchor): Promise<void> {
    await this.publish({
      id,
      type: 'COMMENT',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      isDeleted: true,
      actor: currentUserAlias(),
    });
  }

  // ── Replies (nested under a comment/callout) ──
  public async createReply(
    anchor: CellAnchor,
    commentId: string,
    body: string,
  ): Promise<void> {
    const written = await this.publish({
      type: 'REPLY',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      commentId,
      content: body,
      actor: currentUserAlias(),
    });
    rememberOwnRecord(written?.id);
  }

  public async editReply(
    id: string,
    body: string,
    anchor: CellAnchor,
  ): Promise<void> {
    await this.publish({
      id,
      type: 'REPLY',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      content: body,
      actor: currentUserAlias(),
    });
  }

  public async deleteReply(id: string, anchor: CellAnchor): Promise<void> {
    await this.publish({
      id,
      type: 'REPLY',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      isDeleted: true,
      actor: currentUserAlias(),
    });
  }
}

/** Singleton (portal convention): one shared instance app-wide. */
export const MetricCommentsApi = new MetricCommentsApiClient();
